import { NavLink, useParams } from "react-router";
import { useHtzOrderShowQuery } from "@/services/backend/htz/orders/api/order-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Order } from "@/services/backend/htz/orders/types/order";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DeleteOrderDialog } from "@/routes/processes/[processId]/htz/orders/[orderId]/_components/delete-order-dialog";
import {
  ErrDocumentNotReadyContextProvider,
  useErrDocumentNotReady,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import t from "@/lang/lang";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { HtzBreadcrumbs } from "@/routes/processes/[processId]/htz/_components/htz-breadcrumbs";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";

export function OrderRoute() {
  const { orderId } = useParams();

  const {
    data: order,
    isLoading,
    error,
  } = useHtzOrderShowQuery({
    id: orderId!,
  });

  const breadcrumbs = order && (
    <HtzBreadcrumbs customerId={order.customerId} processId={order.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/htz/${order.processId!}/orders`}>
            {t("Auftragseingänge")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <DocumentNumberById documentId={order.id} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </HtzBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {order && <Page order={order} />}
    </ProcessPageMainTemplate>
  );
}

export function Page({ order }: { order: Order }) {
  return (
    <PersistenceContextProvider>
      <ErrDocumentNotReadyContextProvider>
        <DocumentPage>
          <DocumentPageHeader>
            <TabNavigation />
            <DocumentPageActions>
              <PersistenceStateUsingContext className="mr-2" />
              <CpyDialog
                destinationEntityName={CpyName.HTZOrder}
                destinationId={order.id}
                initialParams={{
                  // We limit the order to offers
                  // from the same process. This
                  // is the 90% default what users
                  // need. If needed, they can change
                  // the filter.
                  process: {
                    active: order.processId !== null,
                    values: [order.processId || NullUUID],
                  },
                  orderBy: [{ field: "updatedAt", dsc: true }],
                }}
              />
              <DeleteOrderDialog orderId={order.id} />
            </DocumentPageActions>
          </DocumentPageHeader>
          <DocumentPageContent>
            <DocumentPageNavigationOutlet />
          </DocumentPageContent>
        </DocumentPage>
      </ErrDocumentNotReadyContextProvider>
    </PersistenceContextProvider>
  );
}

function TabNavigation() {
  const { notReadyError } = useErrDocumentNotReady();

  const hasDataError = hasFieldError(notReadyError, "date");
  const hasPosError = hasFieldError(notReadyError, "positions");
  const hasAddrError = hasFieldError(
    notReadyError,
    "postalAddress",
    "serviceAddress",
  );
  const hasRepError = hasFieldError(notReadyError, "representative");

  const links = [
    { to: "data", label: t("Auftragseingang"), hasError: hasDataError },
    { to: "positions", label: t("Leistungen"), hasError: hasPosError },
    {
      to: "addresses-and-contacts",
      label: t("Adressen & Kontakte"),
      hasError: hasAddrError || hasRepError,
    },
    { to: "files", label: t("Dateien") },
  ];

  return (
    <DocumentPageNavigation>
      {links.map((link) => (
        <DocumentPageNavigationLink
          key={link.to}
          to={link.to}
          hasValidationError={link.hasError}
        >
          {link.label}
        </DocumentPageNavigationLink>
      ))}
    </DocumentPageNavigation>
  );
}
